import { useSyncExternalStore } from 'react';
import { getPreset, type Ruleset } from '@ic/engine';

export interface Store<T> {
  get(): T;
  set(v: T | ((prev: T) => T)): void;
  subscribe(fn: () => void): () => void;
}

/** Tiny observable value persisted in localStorage (per-device conveniences only). */
export function persisted<T extends object>(key: string, initial: T, merge = true): Store<T> {
  let value = initial;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Lists are taken as they are (merging would turn them into objects); a broken list falls back to the default.
      if (Array.isArray(initial)) value = (Array.isArray(parsed) ? parsed : initial) as T;
      else value = merge ? { ...initial, ...parsed } : parsed;
    }
  } catch { /* storage unavailable */ }
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set(v) {
      value = typeof v === 'function' ? (v as (p: T) => T)(value) : v;
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
      subs.forEach((f) => f());
    },
    subscribe(fn) { subs.add(fn); return () => { subs.delete(fn); }; },
  };
}

export const useStore = <T,>(s: Store<T>): T => useSyncExternalStore(s.subscribe, s.get, s.get);

export type Theme = 'system' | 'light' | 'dark';
export interface Settings {
  theme: Theme;
  pieceSet: 'classic' | 'neon';
  boardTheme: 'sand' | 'walnut' | 'emerald' | 'ice' | 'neon';
  sound: boolean;
  coords: boolean;
  animations: boolean;
  /** Rate moves (brilliant, best, blunder...) during games. */
  moveFeedback: boolean;
}

// Versioned key: the warm redesign resets board/piece defaults once.
export const settings = persisted<Settings>('ic.settings.v2', {
  theme: 'system', pieceSet: 'classic', boardTheme: 'sand', sound: true, coords: true, animations: true, moveFeedback: true,
});

/** The ruleset that the next game (any mode) will use. */
export const activeRuleset = persisted<Ruleset>('ic.activeRuleset', getPreset('standard'), false);

export function applyTheme(t: Theme) {
  const el = document.documentElement;
  if (t === 'system') delete el.dataset.theme;
  else el.dataset.theme = t;
}
